import type { JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { t } from 'i18next';
import { EDataTableType, EModuleType, EToolbarButton } from '../../data/Constants';
import { ActionableDataTable } from '../DataTable/ActionableDataTable';
import { deleteLovOption, getLovOption, updateLovOption } from '../../store/states/LovSlice';
import { setAlertMessage } from '../../store/states/AlertMessageSlice';
import type { AppDispatch, AppState } from '../../store';
import type { IDataTableRow, ILovOptionsDialogProps } from '../../../types';
import DraggableComponent from '../DraggableComponent';

/**
 * Dialog for maintaining the options that belong to a single LOV.
 *
 * Shows the options maintenance table with inline edit and delete support. Editing is
 * disabled when the parent LOV is inactive.
 *
 * @param props - Component props
 * @param props.lovCode - Code of the parent LOV whose options are managed
 * @param props.token - Auth token forwarded to the maintenance table
 * @param props.isLovActive - When false, the table is rendered read-only
 * @param props.onClose - Called when the dialog is dismissed
 * @returns The LOV options dialog element
 */
export const LovOptionsManagement = ({
  lovCode,
  lovName,
  isLovActive,
  onClose,
}: ILovOptionsDialogProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const lovOptionsState = useSelector((state: AppState) => state.lovManagement.lovOptions);

  /**
   * Persist an inline option edit, then show a success alert and reload the current page
   *
   * @param _id - Row id from the table; unused because the option code identifies the record
   * @param values - Edited field values, where `isActive` arrives as a 'true'/'false' string
   * @param row - The original row, used to read the option code
   */
  const handleSaveOption = (
    _id: number | string,
    values: Record<string, string>,
    row?: IDataTableRow
  ): void => {
    void dispatch(
      updateLovOption({
        lovCode,
        code: row?.code ?? '',
        name: values.name,
        isActive: values.isActive === 'true',
      })
    ).then((res) => {
      if (res.payload && typeof res.payload === 'object' && !('error' in res.payload)) {
        dispatch(
          setAlertMessage({ show: true, message: t('messages.success.update'), type: 'success' })
        );
        void dispatch(
          getLovOption({
            lovCode,
            page: lovOptionsState.currentPage,
            limit: lovOptionsState.recordsPerPage,
          })
        );
      }
    });
  };

  /**
   * Delete the selected options, then show a success alert and reload from the first page
   *
   * @param codes - Codes of the options to delete
   */
  const handleDeleteOptions = (codes: string[]): void => {
    void dispatch(deleteLovOption({ lovCode, options: codes })).then((res) => {
      if (res.payload && typeof res.payload === 'object' && !('error' in res.payload)) {
        dispatch(
          setAlertMessage({
            show: true,
            message: t('messages.success.delete.general'),
            type: 'success',
          })
        );
        void dispatch(getLovOption({ lovCode, page: 1, limit: lovOptionsState.recordsPerPage }));
      }
    });
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      className="common-dialog-style"
      PaperComponent={DraggableComponent}
      slotProps={{ paper: { sx: { height: '75vh', display: 'flex', flexDirection: 'column' } } }}
    >
      <DialogTitle className="header">{t('labels.lovOptions', { name: lovName })}</DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          overflow: 'hidden',
          '&.MuiDialogContent-root': { paddingTop: '16px' },
          px: 1,
          pb: 0,
          '& .maintenanceTable': { height: '100%' },
        }}
      >
        <ActionableDataTable
          type={EDataTableType.LovOptions}
          moduleType={EModuleType.Lov}
          headerActions={{
            [EToolbarButton.Delete]: { onClick: handleDeleteOptions, showDialog: true },
          }}
          forceLoadOnMount
          loadData={({ page, limit, searchCode }) =>
            void dispatch(getLovOption({ lovCode, page, limit, searchCode }))
          }
          rowActions={{ save: { onClick: handleSaveOption } }}
          readOnly={!isLovActive}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('button.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};
